<template>
    <div class="addressbook-dropdown">
        <template v-if="!isEdit">
            <div class="addressbook-dropdown__header">
                <div class="addressbook-dropdown__title">{{ $t('local_addressbook.favorite') }}</div>
                <button type="button" v-if="canSave && !isAlreadyAdded" v-on:click="saveItem" class="addressbook-dropdown__header-button">
                    <icon-bookmark/>{{ $t('local_addressbook.save_address') }}
                </button>
                <button type="button" v-if="canSave && isAlreadyAdded" v-on:click="removeItem" class="addressbook-dropdown__header-button">
                    <icon-bookmark class="addressbook-dropdown__header-button-icon--filled"/>{{ $t('local_addressbook.remove_address') }}
                </button>
            </div>

            <div class="addressbook-dropdown__items-container" v-if="addressbook.length > 0">
                <desktop-addressbook-item
                    v-for="(item, index) in addressbook"
                    v-bind:address="item.address"
                    v-bind:name="item.name"
                    v-bind:index="index"
                    v-bind:type="item.type"
                    v-on:edit-address-name="editName"
                    v-bind:key="`local_ab_item_${index}`"
                />
            </div>

            <div v-else class="addressbook-dropdown__empty muted">
                <icon-addressbook class="addressbook-dropdown__empty-icon"/>
                {{ $t('local_addressbook.empty') }}
            </div>
        </template>

        <template v-else>
            <div class="addressbook-dropdown__header">
                <button type="button" class="addressbook-dropdown__header-button addressbook-dropdown__header-button--back" v-on:click="cancelEdit">
                    <icon-back/>{{ $t('local_addressbook.back') }}
                </button>
                <ui-address
                    class="muted addressbook-dropdown__edit-address"
                    v-bind:address="editObject.address"
                    v-bind:disabled="true"
                    v-bind:hide-name="true"
                    v-bind:hide-copy="true"
                />
            </div>
            <div class="addressbook-dropdown__edit">
                <input
                    class="addressbook-dropdown__input"
                    type="text"
                    v-bind:placeholder="$t('local_addressbook.title')"
                    spellcheck="false"
                    autocomplete="off"
                    v-model.trim="newName"
                    v-on:keyup.enter="saveEdited"
                    v-on:keyup.esc="cancelEdit"
                    autofocus
                    maxlength="32"
                    ref="editInput"
                />
                <button type="button" v-on:click="saveEdited" class="addressbook-dropdown__submit">
                    {{ $t('local_addressbook.save') }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import IconBack from '@img/icons/lucide/chevron-left.svg?vue';
import IconBookmark from '@img/icons/lucide/bookmark.svg?vue';
import IconAddressbook from '@img/icons/lucide/book-user.svg?vue';
import DesktopAddressbookItem from '~/components/address/Addressbook/DesktopAddressbookItem.vue';

const saveTypes = [
    'address',
    'token',
    'pool',
    'vesting',
    'locker',
    'nft',
    'single_nominator',
    'collection',
    'multisig',
    'lp',
];

export default {
    data() {
        return {
            isEdit: false,
            editObject: undefined,
            newName: undefined,
        };
    },

    computed: {
        isAlreadyAdded() {
            return !!this.addressbook.find(i => i.address === this.presaveAddressbookItem.address);
        },
        addressbook() {
            return this.appLocalAddressbook;
        },
        canSave() {
            const routerName = this.$route.name;

            return saveTypes.includes(routerName);
        },
        ...mapState(['appLocalAddressbook', 'presaveAddressbookItem']),
    },

    methods: {
        saveItem() {
            this.$store.dispatch('rememberAddressbookItem');
        },
        removeItem() {
            this.$store.dispatch('forgetAddressbookItem', { address: this.presaveAddressbookItem.address });
        },
        editName(index) {
            this.isEdit = true;
            this.editObject = this.addressbook[index];
            if (this.addressbook[index]?.name > 0) {
                this.newName = '';
            } else {
                this.newName = this.addressbook[index]?.name || '';
            }
            this.$nextTick(() => this.$refs.editInput.focus());
        },
        cancelEdit() {
            this.isEdit = false;
            this.editObject = undefined;
            this.newName = undefined;
        },
        saveEdited() {
            this.$store.dispatch('editAddressbookItem', { address: this.editObject.address, newName: this.newName });
            this.cancelEdit();
        },
    },

    components: {
        IconBack,
        IconBookmark,
        IconAddressbook,
        DesktopAddressbookItem,
    },
};
</script>

<style lang="scss">
// Same surface as the other header dropdowns (links, settings):
.addressbook-dropdown {
    position: absolute;
    top: 46px;
    right: 0;
    z-index: 1002;
    width: 320px;
    box-sizing: border-box;
    padding: 6px;
    background: var(--surface-elevated-background);
    border: 1px solid var(--card-border-color);
    border-radius: 14px;
    box-shadow: 0 1px 2px var(--card-box-shadow-color), 0 16px 40px -12px var(--card-box-shadow-color);
    font-size: 14px;
    overflow: hidden;
    text-align: left;
    cursor: default;

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        height: 38px;
        padding: 0 4px 0 10px;
        margin-bottom: 6px;
        user-select: none;
        // The separator runs edge to edge, like the rows in the settings dropdown:
        &::after {
            content: '';
            position: absolute;
            left: -6px;
            right: -6px;
            bottom: -3px;
            height: 1px;
            background: var(--card-row-separator);
        }
    }

    &__title {
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--body-text-color);
        white-space: nowrap;
    }

    &__header-button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 30px;
        padding: 0 10px 0 8px;
        border-radius: 8px;
        color: var(--blue-bright);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.01em;
        white-space: nowrap;
        cursor: pointer;
        transition: background-color .12s ease, transform .1s ease;

        svg {
            width: 15px;
            height: 15px;
            stroke: currentColor;
            fill: none;
        }

        &:hover {
            background: var(--icon-button-hover-background);
        }
        &:active {
            transform: scale(.97);
        }
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: -2px;
        }

        &--back {
            margin-left: -6px;
            padding: 0 10px 0 6px;
            gap: 3px;
            color: var(--body-text-color);
            svg {
                width: 16px;
                height: 16px;
                color: var(--body-muted-text-color);
            }
        }
    }

    &__header-button-icon--filled {
        fill: currentColor !important;
    }

    &__items-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
        max-height: 305px;
        overflow-y: auto;
        scrollbar-color: transparent transparent;
        scrollbar-width: none;
        &::-webkit-scrollbar {
            display: none;
        }
    }

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 22px 0 24px;
        font-size: 13px;
        text-align: center;
        user-select: none;
    }

    &__empty-icon {
        width: 22px;
        height: 22px;
        opacity: .5;
    }

    &__edit-address {
        flex-shrink: 1;
        min-width: 0;
        max-width: 170px;
        margin-right: 6px;
        font-size: 12px;
    }

    &__edit {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0 4px 4px;
    }

    &__input {
        box-sizing: border-box;
        width: 100%;
        height: 40px;
        padding: 0 12px;
        border-radius: 10px;
        border: 2px solid var(--card-border-color);
        background: var(--indexpage-search-background-color);
        color: var(--body-text-color);
        font-size: 14px;
        transition: border-color .12s ease;

        &:focus {
            border-color: var(--app-search-field-focused-border-color);
        }
    }

    &__submit {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 40px;
        padding: 0;
        border-radius: 10px;
        background: var(--big-blue-button-background);
        color: #FFF;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: .02em;
        text-transform: uppercase;
        cursor: pointer;
        user-select: none;
        transition: background-color .15s ease, transform .1s ease;

        &:hover {
            background: var(--big-blue-button-hover-background);
        }
        &:active {
            transform: scale(.98);
        }
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: 2px;
        }
    }
}
</style>
